import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { dashFetch, getActiveSite } from "@/api/client";
import type { AssistantTask } from "./assistantTaskTypes";

type AssistantTaskContextValue = {
  tasks: AssistantTask[];
  focusedTask: AssistantTask | null;
  modalOpen: boolean;
  openTask: (taskId: string) => void;
  openTaskModal: (task: AssistantTask) => void;
  closeModal: () => void;
  upsertTask: (task: AssistantTask) => void;
  refreshTask: (taskId: string) => Promise<AssistantTask | null>;
  dismissTask: (taskId: string) => void;
};

const AssistantTaskContext = createContext<AssistantTaskContextValue | null>(null);

export function useAssistantTasks(): AssistantTaskContextValue {
  const ctx = useContext(AssistantTaskContext);
  if (!ctx) throw new Error("useAssistantTasks must be used within AssistantTaskProvider");
  return ctx;
}

export function useOptionalAssistantTasks(): AssistantTaskContextValue | null {
  return useContext(AssistantTaskContext);
}

export function AssistantTaskProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<AssistantTask[]>([]);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const upsertTask = useCallback((task: AssistantTask) => {
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.id === task.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = task;
        return next;
      }
      return [task, ...prev].slice(0, 24);
    });
  }, []);

  const refreshTask = useCallback(async (taskId: string) => {
    const id = String(taskId || "").trim();
    if (!id) return null;
    const domain = getActiveSite();
    const q = domain ? `?domain=${encodeURIComponent(domain)}` : "";
    try {
      const data = await dashFetch<{ ok?: boolean; task?: AssistantTask }>(
        `/dashboard/api/assistant/tasks/${encodeURIComponent(id)}${q}`,
        { cache: "no-store" }
      );
      if (data?.ok && data.task) {
        upsertTask(data.task);
        return data.task;
      }
    } catch {
      /* ignore */
    }
    return null;
  }, [upsertTask]);

  const openTask = useCallback(
    (taskId: string) => {
      setFocusedId(taskId);
      setModalOpen(true);
      void refreshTask(taskId);
    },
    [refreshTask]
  );

  const openTaskModal = useCallback((task: AssistantTask) => {
    upsertTask(task);
    setFocusedId(task.id);
    setModalOpen(true);
  }, [upsertTask]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const dismissTask = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    setFocusedId((cur) => (cur === taskId ? null : cur));
  }, []);

  const focusedTask = useMemo(
    () => tasks.find((t) => t.id === focusedId) || null,
    [tasks, focusedId]
  );

  const hasRunning = tasks.some((t) => t.status === "running");

  useEffect(() => {
    if (!hasRunning) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    const tick = () => {
      const running = tasks.filter((t) => t.status === "running");
      for (const t of running) void refreshTask(t.id);
    };
    tick();
    pollRef.current = setInterval(tick, 900);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasRunning, tasks, refreshTask]);

  const value = useMemo(
    () => ({
      tasks,
      focusedTask,
      modalOpen,
      openTask,
      openTaskModal,
      closeModal,
      upsertTask,
      refreshTask,
      dismissTask
    }),
    [tasks, focusedTask, modalOpen, openTask, openTaskModal, closeModal, upsertTask, refreshTask, dismissTask]
  );

  return <AssistantTaskContext.Provider value={value}>{children}</AssistantTaskContext.Provider>;
}
